import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeOff, type LucideIcon } from 'lucide-react';
import { FormLabel } from './FormLabel';

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  icon?: LucideIcon;
  rightElement?: ReactNode;
  error?: string;
  containerClassName?: string;
  required?: boolean;
  tooltip?: string;
  isDisabled?: boolean;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      icon: Icon,
      rightElement,
      error,
      type,
      className = '',
      containerClassName = '',
      required,
      tooltip,
      isDisabled,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

    return (
      <div className={`space-y-2 ${containerClassName}`}>
        {label && (
          <FormLabel htmlFor={props.id} required={required} tooltip={tooltip}>
            {label}
          </FormLabel>
        )}
        <div className="relative group">
          {Icon && (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-lime-900 transition-colors">
              <Icon size={18} strokeWidth={2.5} />
            </div>
          )}

          <input
            ref={ref}
            type={inputType}
            className={`
              w-full rounded-2xl border bg-white text-sm text-gray-900 placeholder:text-gray-400
              shadow-sm outline-none transition-all duration-200
              ${Icon ? 'pl-11' : 'pl-4'}
              ${isPassword && rightElement ? 'pr-24' : isPassword || rightElement ? 'pr-12' : 'pr-4'}
              py-[13.5px]
              ${
                error
                  ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/5'
                  : 'border-gray-200 group-hover:border-gray-300 focus:border-lime-900 focus:ring-4 focus:ring-lime-900/5'
              }
              disabled:opacity-50 disabled:cursor-not-allowed
              ${className}
            `}
            disabled={isDisabled ?? props.disabled}
            {...props}
          />

          {(rightElement ?? isPassword) && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {rightElement}
              {isPassword && (
                <button
                  type="button"
                  onClick={() => {
                    setShowPassword(!showPassword);
                  }}
                  className="text-gray-400 hover:text-gray-900 transition-colors focus:outline-none p-1"
                >
                  {showPassword ? <EyeOff size={18} strokeWidth={2.5} /> : <Eye size={18} strokeWidth={2.5} />}
                </button>
              )}
            </div>
          )}
        </div>
        {error && <p className="px-1 text-xs font-bold text-red-600 animate-in fade-in slide-in-from-top-1">{error}</p>}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';
